/**
 * FreshCards - Main Application Component
 * Notion-Style Productivity Application
 * 
 * Refactored to use FreshCardsSubsystem for centralized management.
 * Now delegates all core functionality to the subsystem while maintaining
 * the UI layer using Mantine AppShell.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AppShell,
  Burger,
  Group,
  Text,
  ActionIcon,
  useMantineColorScheme,
  useMantineTheme,
  Drawer,
  Stack,
  Button,
  Badge,
  Flex,
  rem,
  Modal,
  TextInput,
  ScrollArea,
  List,
} from '@mantine/core';
import {
  IconFolder,
  IconDatabase,
  IconSparkles,
  IconSun,
  IconMoon,
  IconPlus,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { FreshCardsSubsystem } from './shell/FreshCardsSubsystem';
import SurfaceRenderer from './ui/components/common/SurfaceRenderer';
import './styles/database.css';

// Surface mapping from sidebar IDs to surface names
const surfaceMap: Record<string, string> = {
  'home': 'HomePage',
  'databases': 'DatabaseView',
  'ai': 'AIChat',
  'quicknote': 'QuickNote',
  'pagetree': 'PageTree',
  'pageeditor': 'PageEditor'
};

// Sidebar items - defined outside component for reference
const sidebarItems = [
  {
    id: 'home',
    label: 'Docs',
    icon: <IconFolder size={rem(20)} />,
    phase: 1,
  },
  {
    id: 'databases',
    label: 'DBs',
    icon: <IconDatabase size={rem(20)} />,
    phase: 3,
  },
  {
    id: 'ai',
    label: 'AI',
    icon: <IconSparkles size={rem(20)} />,
    phase: 7,
  },
  {
    id: 'quicknote',
    label: 'Quick Note',
    icon: <IconPlus size={rem(20)} />,
    phase: 1,
  },
  {
    id: 'pagetree',
    label: 'Pages',
    icon: <IconFolder size={rem(20)} />,
    phase: 1,
  },
  {
    id: 'pageeditor',
    label: 'Page Editor',
    icon: <IconDatabase size={rem(20)} />,
    phase: 4,
  }
];

const App: React.FC = () => {
  // Initialize FreshCardsSubsystem
  const [subsystem, setSubsystem] = useState<FreshCardsSubsystem | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize core services and subsystem
  useEffect(() => {
    const initializeSubsystem = async () => {
      try {
        // Create subsystem
        const freshCardsSubsystem = new FreshCardsSubsystem();
        
        // Initialize subsystem
        await freshCardsSubsystem.initialize();
        
        // Set subsystem state
        setSubsystem(freshCardsSubsystem);
        setIsInitialized(true);
        
        console.log('FreshCardsSubsystem initialized successfully');
        
      } catch (error) {
        console.error('Failed to initialize FreshCardsSubsystem:', error);
        setIsInitialized(false);
      }
    };
    
    initializeSubsystem();
  }, []);

  const [active, setActive] = useState('home');
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelSurface, setRightPanelSurface] = useState<string | null>(null);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure(false);
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [commandSearch, setCommandSearch] = useState('');
  
  // Get current surface from subsystem
  const currentSurface = useMemo(() => {
    if (!subsystem) return null;
    const surfaceName = surfaceMap[active] || active;
    return subsystem.getSurfaceRegistry().getSurface(surfaceName);
  }, [subsystem, active]);
  
  // Get action buttons from subsystem
  const actionButtons = useMemo(() => {
    if (!subsystem) return [];
    return subsystem.getActionButtons();
  }, [subsystem]);
  
  // Handle navigation through subsystem
  const handleNavigate = (sidebarId: string, params?: any) => {
    if (!subsystem) return;
    
    // Map sidebar ID to surface name
    const surfaceName = surfaceMap[sidebarId] || sidebarId;
    
    try {
      // Use subsystem navigation
      subsystem.navigateTo(surfaceName, params);
      
      // Update local state
      setActive(sidebarId);
      
      // Handle peek surfaces
      const surface = subsystem.getSurfaceRegistry().getSurface(surfaceName);
      if (surface && surface.type === 'peek') {
        setRightPanelSurface(surfaceName);
        setRightPanelOpen(true);
        setIsRightPanelCollapsed(false);
      } else {
        setRightPanelSurface(null);
        setRightPanelOpen(false);
      }
      
    } catch (error) {
      console.error('Navigation failed:', error);
    }
  };
  
  // Execute command through subsystem
  const executeCommand = async (commandName: string) => {
    if (!subsystem) return;
    
    try {
      await subsystem.executeCommand(commandName);
      setCommandPaletteOpen(false);
      setCommandSearch('');
    } catch (error) {
      console.error('Command execution failed:', error);
    }
  };

  // Keyboard shortcut for command palette (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
        setCommandSearch('');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!subsystem || !commandSearch) return [];
    const searchLower = commandSearch.toLowerCase();
    return subsystem.getAllCommands().filter((cmd: any) =>
      cmd.name.toLowerCase().includes(searchLower) ||
      (cmd.description && cmd.description.toLowerCase().includes(searchLower))
    );
  }, [subsystem, commandSearch]);

  // Handle surface state updates from subsystem
  useEffect(() => {
    if (!subsystem) return;
    
    const handleSurfaceChanged = (data: any) => {
      setActive(data.surfaceName);
    };
    
    const handleNavigation = (data: any) => {
      const sidebarId = Object.keys(surfaceMap).find(key => surfaceMap[key] === data.surface) || data.surface;
      setActive(sidebarId);
    };
    
    subsystem.getEventBus().on('surface:changed', handleSurfaceChanged);
    subsystem.getEventBus().on('navigated', handleNavigation);
    
    return () => {
      subsystem.getEventBus().off('surface:changed', handleSurfaceChanged);
      subsystem.getEventBus().off('navigated', handleNavigation);
    };
  }, [subsystem]);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: { base: 250, md: 280 },
        breakpoint: 'md',
        collapsed: { mobile: !mobileOpened, desktop: false }
      }}
      padding="md"
      bg={colorScheme === 'dark' ? theme.colors['slate'][9] : theme.colors.gray[0]}
    >
      {/* Mobile Header */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="md" size="sm" />
            <Text fw={700} size="lg">
              FreshCards
            </Text>
          </Group>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color={colorScheme === 'dark' ? 'yellow' : 'dark'}
              onClick={() => toggleColorScheme()}
              title="Toggle theme"
            >
              {colorScheme === 'dark' ? (
                <IconSun size={rem(18)} />
              ) : (
                <IconMoon size={rem(18)} />
              )}
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Desktop Navbar - visible on desktop */}
      <AppShell.Navbar p="md" visibleFrom="md">
        <Stack gap="xs">
          {sidebarItems.map((item) => (
            <Button
              key={item.id}
              variant={active === item.id ? 'filled' : 'light'}
              leftSection={item.icon}
              onClick={() => handleNavigate(item.id)}
              fullWidth
              justify="flex-start"
              color={active === item.id ? 'blue' as any : undefined}
            >
              <Group justify="space-between" w="100%">
                <span>{item.label}</span>
                <Badge size="xs" variant="light">
                  {item.phase}
                </Badge>
              </Group>
            </Button>
          ))}
        </Stack>
      </AppShell.Navbar>

      {/* Desktop Header - visible on desktop */}
      <AppShell.Header visibleFrom="sm">
        <Group h="100%" px="md" justify="space-between">
          <Text fw={700} size="lg">
            FreshCards
          </Text>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color={colorScheme === 'dark' ? 'yellow' : 'dark'}
              onClick={() => toggleColorScheme()}
              title="Toggle theme"
            >
              {colorScheme === 'dark' ? (
                <IconSun size={rem(18)} />
              ) : (
                <IconMoon size={rem(18)} />
              )}
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile Sidebar - controlled by burger menu */}
      <AppShell.Navbar p="md" hiddenFrom="md">
        <Stack gap="xs">
          {sidebarItems.map((item) => (
            <Button
              key={item.id}
              variant={active === item.id ? 'filled' : 'light'}
              leftSection={item.icon}
              onClick={() => handleNavigate(item.id)}
              fullWidth
              justify="flex-start"
              color={active === item.id ? 'blue' as any : undefined}
            >
              <Group justify="space-between" w="100%">
                <span>{item.label}</span>
                <Badge size="xs" variant="light">
                  {item.phase}
                </Badge>
              </Group>
            </Button>
          ))}
        </Stack>
      </AppShell.Navbar>

      {/* Main Content */}
      <AppShell.Main>
        {isInitialized ? (
          <SurfaceRenderer
            surface={currentSurface}
            props={currentSurface?.context || {}}
            actions={actionButtons}
            currentPageId={subsystem?.getCurrentPageId()}
          />
        ) : (
          <Flex h="100%" align="center" justify="center">
            <Text size="lg" c="dimmed">Initializing FreshCards...</Text>
          </Flex>
        )}
      </AppShell.Main>

      {/* Right Panel Drawer */}
      <Drawer
        opened={rightPanelOpen}
        onClose={() => setRightPanelOpen(false)}
        title="Peek"
        size="md"
        position="right"
        padding="md"
        overlayProps={{ color: colorScheme === 'dark' ? 'rgba(0, 0, 0, .5)' : 'rgba(0, 0, 0, .05)' }}
      >
        {rightPanelSurface && !isRightPanelCollapsed && subsystem && (
          <SurfaceRenderer
            surface={subsystem.getSurfaceRegistry().getSurface(rightPanelSurface) || null}
            props={subsystem.getSurfaceRegistry().getSurface(rightPanelSurface)?.context || {}}
          />
        )}
        {rightPanelSurface && isRightPanelCollapsed && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">Peek surfaces appear here</Text>
          </Stack>
        )}
      </Drawer>
      
      {/* Command Palette */}
      <Modal
        opened={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        title="Commands"
        size="lg"
        centered
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap="md">
          <TextInput
            placeholder="Search commands (Ctrl+K)"
            value={commandSearch}
            onChange={(e) => setCommandSearch(e.currentTarget.value)}
            autoFocus
            leftSection={<Text size="sm" c="dimmed">Ctrl+K</Text>}
          />
          <ScrollArea h={400}>
            <List>
              {filteredCommands.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center">No commands found</Text>
              ) : (
                filteredCommands.map((command: any) => (
                  <List.Item
                    key={command.name}
                    onClick={() => executeCommand(command.name)}
                    style={{ cursor: 'pointer', padding: '8px 0' }}
                  >
                    <Group justify="space-between">
                      <Text fw={600}>{command.name}</Text>
                      <Text size="sm" c="dimmed">{command.moduleName}</Text>
                    </Group>
                    {command.description && (
                      <Text size="sm" c="dimmed" mt={4}>{command.description}</Text>
                    )}
                  </List.Item>
                ))
              )}
            </List>
          </ScrollArea>
        </Stack>
      </Modal>
    </AppShell>
  );
};

export default App;